import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Space,
  Tabs,
  Card,
  Button,
  Select,
  Row,
  Col,
  DatePicker,
  Table,
  Pagination,
  message,
  Form,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useQuery, gql, useLazyQuery, useMutation } from '@apollo/client';
import moment from 'moment';
import formatAmount from 'indian-currency-formatter';

import { REPORT_AGENCY_MANAGER_FIELDS } from '../../apollo/fragments';
import {
  CATEGORIES_GET_QUERY,
  EMPLOYEE_LOOKUP_BY_ROLECODE,
} from '../../apollo/queries';
import {
  updateGridOptions,
  updateGridMetaData,
  updateGridFilters,
} from '../../store/reducers/reportSlice';
import * as Config from '../../appConfig';

var fileDownload = require('js-file-download');

const AGENCY_MANAGER_REPORT_QUERY = gql`
  ${REPORT_AGENCY_MANAGER_FIELDS}
  query AGMReport(
    $first: Int
    $page: Int
    $filters: [QueryGetAgencyManagerReportFilter]
  ) {
    agencyManagerReport(first: $first, page: $page, filters: $filters) {
      paginatorInfo {
        count
        currentPage
        total
      }
      data {
        ...AgencyManagerReportFields
      }
    }
  }
`;

const { Option } = Select;
const { TabPane } = Tabs;

const AgencyManagerReport = ({ history, match, gridId }) => {
  const { metaData, grids } = useSelector((state) => state.report);
  const {
    user: { ability },
  } = useSelector((state) => state.login);
  const dispatch = useDispatch();
  const [columns, setColumns] = useState([]);
  const [exportInProgress, setExportInProgress] = useState(false);

  const grid = grids.find((i) => i.id === gridId);
  const locationFilter = grid.metaData.filters.find(
    (i) => i.column === 'LOCATION'
  )
    ? grid.metaData.filters.find((i) => i.column === 'LOCATION').value
    : [];
  const dateFilter = grid.metaData.filters.find((i) => i.column === 'FOR_MONTH')
    ? grid.metaData.filters.find((i) => i.column === 'FOR_MONTH').value
    : '';

  const queryVariables = useMemo(() => {
    return {
      first: grid.metaData.pagination.pageSize,
      page: grid.metaData.pagination.page,
      filters: grid.metaData.filters,
    };
  }, [
    grid.metaData.pagination.pageSize,
    grid.metaData.pagination.page,
    grid.metaData.filters,
  ]);

  const { loading: loadingCategory, data: catData } = useQuery(
    CATEGORIES_GET_QUERY,
    {
      variables: {
        type: 'PRODUCT_TYPE',
      },
    }
  );

  const { loading: loadingDeductionsTypes, data: deductionTypes } = useQuery(
    CATEGORIES_GET_QUERY,
    {
      variables: {
        type: 'DEDUCTION_TYPE',
      },
    }
  );

  const { loading: loadingLocations, data: locations } = useQuery(
    CATEGORIES_GET_QUERY,
    {
      variables: {
        type: 'LOCATION',
      },
    }
  );

  const { loading: loadingAM, data: ams } = useQuery(
    EMPLOYEE_LOOKUP_BY_ROLECODE,
    {
      variables: {
        roleCode: 'AM',
      },
    }
  );

  const { loading, data, error, refetch } = useQuery(
    AGENCY_MANAGER_REPORT_QUERY,
    {
      variables: queryVariables,
    }
  );

  useEffect(() => {
    if (grid) {
      console.log('refetch for metaData with///', queryVariables);
      refetch(queryVariables);
    }
  }, [dispatch, refetch, grid, queryVariables]);

  useEffect(() => {
    if (
      catData &&
      catData.categories &&
      deductionTypes &&
      deductionTypes.categories
    ) {
      console.log('categories fetched ///', catData.categories);
      const catgoriesWithTotal = [...catData.categories, { code: 'TOTAL' }];
      const deductionsWithTotal = [
        ...deductionTypes.categories,
        { code: 'TOTAL' },
      ];
      let columns2 = [
        {
          title: 'AM CODE',
          dataIndex: 'am_code',
          key: 'am_code',
          serverKey: 'AM_EMP_ID',
          dataType: 'string',
          fixed: 'left',
        },
        {
          title: 'LOC',
          dataIndex: 'location_name',
          key: 'location_name',
          serverKey: 'LOC',
          dataType: 'string',
          fixed: 'left',
        },
        {
          title: 'EMP CODE',
          dataIndex: 'emp_code',
          key: 'emp_code',
          serverKey: 'EMP_ID',
          dataType: 'string',
          fixed: 'left',
        },
        {
          title: 'EMP NAME',
          dataIndex: 'name',
          key: 'name',
          serverKey: 'NAME',
          dataType: 'string',
          ellipsis: true,
          dataType: 'string',
          render: (value, record) => {
            return record && `${record.first_name} ${record.last_name}`;
          },
        },
        {
          title: 'DESG',
          dataIndex: 'designation_name',
          key: 'designation_name',
          serverKey: 'DESG',
          dataType: 'string',
        },
        {
          title: 'STATUS',
          dataIndex: 'employee_status',
          key: 'employee_status',
          serverKey: 'STATUS',
          dataType: 'string',
        },
        {
          title: 'BASIC',
          dataIndex: 'basic',
          key: 'basic',
          serverKey: 'BASIC',
          dataType: 'cell-currency',
          className: 'cell-currency',
        },
        {
          title: 'CONVEYANCE',
          dataIndex: 'conveyance',
          key: 'conveyance',
          serverKey: 'CONVEYANCE',
          dataType: 'cell-currency',
          className: 'cell-currency',
        },
        {
          title: 'FE INCENTIVES',
          dataIndex: 'fe_incentive',
          key: 'fe_incentive',
          dataType: 'cell-currency',
          children: catgoriesWithTotal.map((productType) => {
            return {
              title: productType.code,
              dataIndex: 'fe_incentive',
              key: `fe_incentive_${productType.code}`,
              serverKey: `FE_${productType.code}`,
              dataType: 'cell-currency',
              className: 'cell-currency',
              render: (fe_incentive) => {
                return formatAmount(
                  fe_incentive.find((i) => i.name === productType.code).value ||
                    0
                );
              },
            };
          }),
        },
        {
          title: 'TL INCENTIVES',
          dataIndex: 'tl_incentive',
          key: 'tl_incentive',
          dataType: 'cell-currency',
          children: catgoriesWithTotal.map((productType) => {
            return {
              title: productType.code,
              dataIndex: 'tl_incentive',
              key: `tl_incentive_${productType.code}`,
              dataType: 'cell-currency',
              serverKey: `TL_${productType.code}`,
              className: 'cell-currency',
              render: (tl_incentive) => {
                return formatAmount(
                  tl_incentive.find((i) => i.name === productType.code).value ||
                    0
                );
              },
            };
          }),
        },
        {
          title: 'SPL PAY',
          dataIndex: 'spl_allowance',
          key: 'spl_allowance',
          serverKey: 'SPL PAY',
          dataType: 'cell-currency',
          className: 'cell-currency',
        },
        {
          title: 'GROSS PAY',
          dataIndex: 'gross_pay',
          key: 'gross_pay',
          serverKey: 'GROSS PAY',
          dataType: 'cell-currency',
          className: 'cell-currency',
        },
        {
          title: 'DEDUCTIONS',
          dataIndex: 'deductions',
          key: 'deductions',
          serverKey: 'DEDUCTIONS',
          dataType: 'cell-currency',
          children: deductionsWithTotal.map((deductionType) => {
            return {
              title: deductionType.code,
              dataIndex: 'deductions',
              key: `deductions_${deductionType.code}`,
              dataType: 'cell-currency',
              serverKey: `DEDUCT_${deductionType.code}`,
              className: 'cell-currency',
              render: (deductions) => {
                return formatAmount(
                  deductions.find((i) => i.name === deductionType.code).value ||
                    0
                );
              },
            };
          }),
        },
        {
          title: 'NET PAY',
          dataIndex: 'net_pay',
          key: 'net_pay',
          serverKey: 'NET PAY',
          dataType: 'cell-currency',
          className: 'cell-currency',
          fixed: 'right',
        },
      ].filter((i) => !i.hidden);
      setColumns(columns2);
    }
  }, [catData, deductionTypes]);

  const onChange = (updatedMetaData) => {
    console.log('onChange///', updatedMetaData);
    dispatch(
      updateGridMetaData({
        gridId,
        metaData: updatedMetaData,
      })
    );
  };

  const handleApplyFilter = (values) => {
    console.log('handleApplyFilter ///', values);
    dispatch(
      updateGridFilters({
        gridId,
        filters: [
          {
            column: 'LOCATION',
            value: values.location || '',
          },
          {
            column: 'FOR_MONTH',
            value: values.month
              ? values.month.format(Config.DATE_FORMAT_SERVER)
              : '',
          },
          {
            column: 'AM',
            value: values.am || '',
          },
        ],
      })
    );
  };

  const onExport = (onFinish) => {
    setExportInProgress(true);
    console.log('onExport Clicked');
    const columnsToExport = [];
    columns.forEach((col) => {
      if (col.children) {
        col.children.forEach((innerCol) => {
          columnsToExport.push({
            column: innerCol.serverKey,
            dataType: innerCol.dataType,
            heading: innerCol.serverKey,
          });
        });
      } else {
        columnsToExport.push({
          column: col.dataIndex,
          dataType: col.dataType,
          heading: col.serverKey,
        });
      }
    });

    fetch(
      `${process.env.REACT_APP_API_BASE_URL}/report/agency-manager-report-export`,
      {
        method: 'POST',
        cache: 'no-cache',
        headers: new Headers({
          Authorization: 'Bearer ' + localStorage.getItem('token'),
        }),
        body: JSON.stringify({
          filters: grid.metaData.filters,
          columns: columnsToExport,
        }),
      }
    )
      .then((response) => {
        if (response.status === 200) {
          return response.blob();
        } else {
          onFinish('error');
          throw new Error('Error');
        }
      })
      .then((blob) => {
        fileDownload(
          blob,
          `${gridId}-${new Date()
            .toLocaleString()
            .replaceAll('/', '-')
            .replace(',', '')
            .replaceAll(' ', '_')}.xlsx`
        );
        setExportInProgress(false);
      })
      .catch((e) => {
        setExportInProgress(false);
        message.error('Something went wrong, Please try again!');
      });
  };

  const handleChange = () => {};

  return (
    <Card className="expenseCard" style={{ minHeight: '200px' }}>
      <Row>
        <Col xs={24} md={22} style={{ marginBottom: '1rem' }}>
          <Space>
            <Form
              name="basic"
              initialValues={{
                location: locationFilter,
                month: dateFilter && moment(dateFilter),
              }}
              onFinish={handleApplyFilter}
              autoComplete="off"
              layout="inline"
            >
              <Form.Item name="location">
                <Select
                  mode="multiple"
                  allowClear
                  style={{ minWidth: 150, maxWidth: 300 }}
                  placeholder="Any Location"
                >
                  {locations &&
                    locations.categories &&
                    locations.categories.map((location) => {
                      return (
                        <Option value={location.id}>{location.code}</Option>
                      );
                    })}
                </Select>
              </Form.Item>

              <Form.Item name="am">
                <Select style={{ width: 150 }} placeholder="Any AM" allowClear>
                  {ams &&
                    ams.employeeByRoleCode &&
                    ams.employeeByRoleCode.map((am) => {
                      return (
                        <Option value={am.id}>
                          {am.first_name} {am.last_name}
                        </Option>
                      );
                    })}
                </Select>
              </Form.Item>

              <Form.Item name="month">
                <DatePicker format={'MM/YYYY'} picker="month" />
              </Form.Item>

              <Button type="primary" htmlType="submit">
                Apply Filter
              </Button>
            </Form>
          </Space>
        </Col>
        <Col
          xs={24}
          md={2}
          style={{ marginBottom: '1rem', textAlign: 'right' }}
        >
          <Space>
            <Button
              type="secondary"
              onClick={onExport}
              icon={<DownloadOutlined />}
              loading={exportInProgress}
              disabled={
                exportInProgress ||
                (data && data.agencyManagerReport.data.length <= 0)
              }
            >
              Export
            </Button>
          </Space>
        </Col>
      </Row>
      {columns.length && data && (
        <div className="dataGrid">
          <Table
            size="small"
            bordered={true}
            columns={columns}
            // dataSource={getMockData(20)}
            dataSource={data.agencyManagerReport.data}
            scroll={{ x: 3500 }}
            pagination={false}
          />
          {data.agencyManagerReport.data.length > 0 && (
            <Pagination
              // size="small"
              {...grid.metaData.pagination}
              total={data.agencyManagerReport.paginatorInfo.total}
              onChange={(page, pageSize) => {
                onChange({
                  pagination: {
                    ...grid.metaData.pagination,
                    page: page,
                    current: page,
                    pageSize: pageSize,
                  },
                });
              }}
              showTotal={(total, range) =>
                `Showing ${range[0]}-${range[1]} of ${total} records`
              }
            />
          )}
        </div>
      )}
    </Card>
  );
};

export default AgencyManagerReport;
